import React from 'react';

import { ProfileTitle } from '@/core/components/ProfileDetails/components';
import UpperTab from '@/core/components/UpperTab';
import UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import Store from '@/core/lib/new-architecture/store';
import type { Badge } from '@/core/lib/new-architecture/store/badges.store';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import BadgesTab from '@/core/pages/profile/PublicProfile/Status/BadgesTab';

const ns = 'profile.profile-public';

const getBadgesInfos = (allBadges: Badge[] | null, filterCat: 'weight' | 'co2' | 'event') => {
  const badgesInTheCat = allBadges?.filter(({ cat }) => cat === filterCat);
  return { badges: badgesInTheCat || [], count: badgesInTheCat?.filter(({ isChecked }) => isChecked).length };
};

const toPercent = (value: number, max: number) => (max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0);

const ProgressBar: React.FC<{ percent: number; className: string }> = ({ percent, className }) => (
  <div className={`overflow-hidden rounded-10 bg-neutral-50 ${className}`}>
    <div className="h-full rounded-10 bg-lagon-700 transition-all" style={{ width: `${percent}%` }} />
  </div>
);

export const BadgesPanel: React.FC<{ user: UserDomain }> = ({ user }) => {
  const { t } = useTranslationContext(['common', ns]);

  const { data: badges, isLoading } = Store.badges.useBadges();

  if (isLoading) return null;

  const weightBadges = getBadgesInfos(badges, 'weight');
  const co2Badges = getBadgesInfos(badges, 'co2');
  const eventBadges = getBadgesInfos(badges, 'event');

  const checkedCount = badges?.filter(({ isChecked }) => isChecked).length ?? 0;
  const percent = toPercent(checkedCount, badges?.length ?? 0);

  return (
    <>
      <div className="space-y-1">
        <ProfileTitle tag="h2">{t('status.badges.title', { ns })}</ProfileTitle>
        <p className="text-caption-primary text-content-placeholder">{t('status.badges.unlocked', { ns, checkedCount, count: badges?.length ?? 0 })}</p>
      </div>
      <ProgressBar percent={percent} className="h-[6px]" />

      <UpperTab
        tabs={[
          {
            name: t('status.tabs.weight', { ns }),
            badge: ` (${weightBadges.count})`,
            content: <BadgesTab cat="weight" badges={weightBadges.badges} userValue={user.totalWeight} hasProgressBarOnCard />,
          },
          {
            name: t('status.tabs.co2', { ns }),
            badge: ` (${co2Badges.count})`,
            content: <BadgesTab cat="co2" badges={co2Badges.badges} userValue={user.totalCo2} hasProgressBarOnCard />,
          },
          {
            name: t('status.tabs.event', { ns }),
            badge: ` (${eventBadges.count})`,
            content: <BadgesTab cat="event" badges={eventBadges.badges} />,
          },
        ]}
      />
    </>
  );
};
